import React from 'react';
import { Tooltip } from 'react-tooltip';

import { getConfig } from 'functions/getConfig';
import {
  QuestionOuterContainer,
  QuestionContainer,
  UpperLeftCornerContainer,
  QuestionIndexAndCategory,
  DifficultyNameContainer,
  TimeCornerDisplay,
  EditButton,
  QuestionsAndOptionsContainer,
  QuestionValue,
  AnswersValue,
  OptionContainer,
  OptionAndAnsweredContainer,
  AnsweredComponent,
  SkillTag,
  QuestionAndSkillComponent,
  TimeSpentContainer,
  TimeAwayDisplay,
  OptionCopiedIcon,
  EventIcon,
  NoTextAnswer,
} from './styles';
import { questionTypes } from 'constants/tests';

export const QuestionDifficulties = {
  VERY_HARD: {
    id: 'very_hard',
    name: 'Very Hard',
    limit: 1150,
  },
  HARD: {
    id: 'hard',
    name: 'Hard',
    limit: 1050,
  },
  MEDIUM: {
    id: 'medium',
    name: 'Medium',
    limit: 950,
  },
  EASY: {
    id: 'easy',
    name: 'Easy',
    limit: 850, // above
  },
  VERY_EASY: {
    id: 'very_easy',
    name: 'Very Easy',
    limit: 850, // below
  },
};

export default React.memo(({ report, isSuperAdmin }) => {
  const config = getConfig();
  const isInductiveTest = report.test.name === 'Inductive test';

  const getQuestionDifficulty = (eloRating) => {
    if (eloRating > QuestionDifficulties.VERY_HARD.limit) {
      return QuestionDifficulties.VERY_HARD;
    } else if (eloRating > QuestionDifficulties.HARD.limit) {
      return QuestionDifficulties.HARD;
    } else if (eloRating > QuestionDifficulties.MEDIUM.limit) {
      return QuestionDifficulties.MEDIUM;
    } else if (eloRating > QuestionDifficulties.EASY.limit) {
      return QuestionDifficulties.EASY;
    }
    return QuestionDifficulties.VERY_EASY;
  };

  const getDisplayTime = (milliseconds) => {
    let minutes = Math.floor(milliseconds / 60000);
    let seconds = ((milliseconds % 60000) / 1000).toFixed(0);
    return seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  };

  const renderOptions = (question) => {
    if (!question.options) return;
    let options = JSON.parse(JSON.stringify(question.options));
    if (!question.answeredOption)
      options.unshift({ value: '<p><i className="fa fa-user"></i>  No answer given</p>', skipped: true });
    if (question.questionType === questionTypes.FREE_TEXT) {
      if (question.textAnswer === null || question.textAnswer === undefined || question.textAnswer === '')
        return <NoTextAnswer>The candidate did not answer the question</NoTextAnswer>;
      else
        return (
          <OptionAndAnsweredContainer
            dangerouslySetInnerHTML={{
              __html: question.textAnswer,
            }}
          ></OptionAndAnsweredContainer>
        );
    }

    return options.map((option, index) => (
      <OptionAndAnsweredContainer
        isInductiveTest={isInductiveTest}
        key={index}
        isCorrect={(question.correct && option.correct) || option.correct}
        isWrong={
          (question.correct === false && question.answeredOption === option._id) ||
          (!question.answeredOption && !option._id)
        }
      >
        {!option.skipped && <AnsweredComponent>Answered: {option.totalAnswered ?? 0}</AnsweredComponent>}
        {question?.optionsCopied?.includes(option._id) && (
          <OptionCopiedIcon
            data-tooltip-id="optionCopied"
            data-tooltip-html="Option text has been copied by the candidate"
          />
        )}
        <OptionContainer dangerouslySetInnerHTML={{ __html: option.value }}></OptionContainer>
      </OptionAndAnsweredContainer>
    ));
  };

  const getRightClickEventText = (question) => {
    if (question.isRightClick && question.isRightClickOnImage)
      return 'Candidate has right clicked on the page and image';
    if (question.isRightClick && !question.isRightClickOnImage) return 'Candidate has right clicked on the page';
    if (!question.isRightClick && question.isRightClickOnImage) return 'Candidate has right clicked on the image';
  };

  return (
    <>
      {report.questions.map((question, index) => (
        <QuestionOuterContainer key={`question${index}`} id={`question${index}`}>
          <QuestionContainer>
            <Tooltip id="questionDifficulty" place="top" />
            <Tooltip id="timeSpent" place="right" />
            <Tooltip id="timeAway" place="right" />
            <Tooltip id="copiedQuestion" place="right" />
            <Tooltip id="optionCopied" place="bottom" />
            <Tooltip id="rightClick" place="right" />
            <Tooltip id="allTextCopied" place="right" />
            <UpperLeftCornerContainer>
              {question.category && question.category != null && (
                <QuestionIndexAndCategory>
                  {index + 1}. {question.category}
                </QuestionIndexAndCategory>
              )}
              <TimeSpentContainer>
                {question.timeSpentOnThisQuestionInMs && (
                  <TimeCornerDisplay
                    data-tooltip-id="timeSpent"
                    data-tooltip-html="Time spent on this question <br/>(min:sec)"
                  >
                    <i className="fa-regular fa-clock clock" />
                    {` ${getDisplayTime(question.timeSpentOnThisQuestionInMs)}`}
                  </TimeCornerDisplay>
                )}
                {question.inactiveTime && (
                  <TimeAwayDisplay
                    data-tooltip-id="timeAway"
                    data-tooltip-html="Time spent away from the tab (min:sec)"
                  >
                    <i className="fa-regular fa-clock clock" />
                    {` ${getDisplayTime(question.inactiveTime)}`}
                  </TimeAwayDisplay>
                )}
              </TimeSpentContainer>
              {question.isQuestionCopied && (
                <EventIcon
                  className="fa fa-clone"
                  data-tooltip-id="copiedQuestion"
                  data-tooltip-html="Question text has been copied by the candidate"
                />
              )}
              {(question.isRightClickOnImage || question.isRightClick) && (
                <EventIcon
                  className="fa fa-mouse-pointer"
                  data-tooltip-id="rightClick"
                  data-tooltip-html={getRightClickEventText(question)}
                />
              )}
              {question.isAllTextCopied && (
                <EventIcon
                  className="fa fa-exclamation-circle"
                  data-tooltip-id="allTextCopied"
                  data-tooltip-html="Whole question and options has been copied by the candidate"
                />
              )}
            </UpperLeftCornerContainer>
            {report.test.adaptiveTest && (
              <DifficultyNameContainer
                data-tooltip-id="questionDifficulty"
                level={getQuestionDifficulty(question.rating).id}
                data-tooltip-content="The difficulty is based on previous results by other test takers"
              >
                {getQuestionDifficulty(question.rating).name}
              </DifficultyNameContainer>
            )}
            {isSuperAdmin && (
              <EditButton
                href={`${config.REACT_URL}/multichoice-creator/${report.test._id}/${question._id}`}
                target="_blank"
              >
                <i className="fa fa-edit"></i>
                {` `}Edit
              </EditButton>
            )}
            <QuestionsAndOptionsContainer>
              <QuestionAndSkillComponent>
                <QuestionValue dangerouslySetInnerHTML={{ __html: question.value }}></QuestionValue>
                {question.elo && isSuperAdmin && (
                  <SkillTag dangerouslySetInnerHTML={{ __html: question.elo }}></SkillTag>
                )}
              </QuestionAndSkillComponent>
              <AnswersValue>{renderOptions(question)}</AnswersValue>
            </QuestionsAndOptionsContainer>
          </QuestionContainer>
        </QuestionOuterContainer>
      ))}
    </>
  );
});
